// Passcode gate logic — ported from ~/avengers-match/src/passcodeGate.ts.
// Pure functions (no DOM); the React UI lives in PasscodeGate.tsx.
// SHA-256 of the entered code is compared against VITE_PASSCODE_HASH (lowercase hex).
// One successful unlock keeps the app open for 30 days on this device.

const STORAGE_KEY = 'crack-the-code:passcode-unlocked-at';
const UNLOCK_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
const EXPECTED_HASH = (import.meta.env.VITE_PASSCODE_HASH ?? '').toLowerCase();

/** True once a real 64-hex-char passcode hash has been configured. */
export function passcodeConfigured(): boolean {
  return /^[0-9a-f]{64}$/.test(EXPECTED_HASH);
}

export async function sha256Hex(s: string): Promise<string> {
  const buf = new TextEncoder().encode(s);
  const hash = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(hash))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

export function isUnlocked(): boolean {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (!raw) return false;
  const ts = Number(raw);
  if (!Number.isFinite(ts)) return false;
  return Date.now() - ts < UNLOCK_WINDOW_MS;
}

function recordUnlock(): void {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

/**
 * Check an entered code. Returns true (and records the unlock) on success.
 * Dev convenience: if no passcode has been configured yet, any 4 digits unlock —
 * so the first iPad play-test isn't blocked before a hash is set.
 */
export async function tryUnlock(entered: string): Promise<boolean> {
  if (!passcodeConfigured()) {
    recordUnlock();
    return true;
  }
  const ok = (await sha256Hex(entered)) === EXPECTED_HASH;
  if (ok) recordUnlock();
  return ok;
}
